import React, { useState } from 'react';
import {
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Card,
  CardContent,
  CardHeader,
} from '@material-ui/core';

import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useDispatch } from 'react-redux';
import 'translations/translations';
import { SawmillResponse, SawmillUserResponse } from '.generated/api';
import { useTranslation } from 'react-i18next';
import 'theme/iconClasses.css';

import { primaryColor } from 'theme/theme';
import { CreateEditSawmillUserDialog } from './CreateEditSawmillUserDialog';
import { sawmillActions } from 'actions/sawmillActions';
import ButtonSpinner from 'components/spinners/ButtonSpinner';
import { notificationActions } from 'actions/notificationActions';

const useStyles = makeStyles((theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  marginRight: {
    marginRight: '0.6rem',
  },
  root: {
    padding: '0.1rem 1rem',
    minWidth: '8.5rem',
  },
  plusIcon: {
    display: 'inline-block',
    fontSize: '1.5rem',
    lineHeight: 0,
    marginRight: '0.5rem',
  },
  addButton: { marginLeft: '0' },
  actionsButton: {
    padding: '0',
  },
  buttonRoot: {
    marginRight: 0,
  },
  content: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
  },
  table: {
    color: primaryColor(),
  },
  tableHead: {
    backgroundColor: 'rgba(240, 241, 243, 1)',
    color: primaryColor(),
  },
  tableHeadCell: {
    fontWeight: 'bold',
    color: primaryColor(),
    padding: '0.7rem 1rem 0.5rem 1rem',
  },
  tableCell: {
    color: primaryColor(),
  },
  tableRow: {
    textDecoration: 'none',
  },
  rowInactive: {
    background: 'rgba(240, 241, 243, 1)',
    opacity: '0.6',
  },
  textBold: {
    fontWeight: 'bold',
    color: primaryColor(),
  },
  preDataView: {
    marginTop: '3rem',
    textAlign: 'center',
  },
  error: {
    color: theme.palette.error.main,
  },
  menu: {
    padding: '0',
  },
}));

interface SawmillCardProps {
  sawmill: SawmillResponse;
  onEdit?: (sawmill: SawmillResponse) => void;
  onDelete?: (sawmill: SawmillResponse) => void;
}

const SawmillCard: React.FC<SawmillCardProps> = ({
  sawmill,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();
  const classes = useStyles();

  const [sawmillUsersLoading, setSawmillUsersLoading] = useState<
    SawmillUserResponse[]
  >([]);

  const [activeSawmillUser, setActiveSawmillUser] = useState<
    SawmillUserResponse | undefined
  >();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setActiveSawmillUser(undefined);
  };

  const handleEditSawmill = () => {
    handleHideContextActions();
    onEdit && onEdit(sawmill);
  };

  const handleDeleteSawmill = () => {
    handleHideContextActions();
    onDelete && onDelete(sawmill);
  };

  const handleAddSawmillUser = () => {
    setAnchorEl(null);
    setDialogOpen(true);
  };

  const handleEditSawmillUser = () => {
    handleHideContextActions();
    setDialogOpen(true);
  };

  const handleDeleteSawmillUser = () => {
    handleHideContextActions();
    if (activeSawmillUser) {
      setSawmillUsersLoading((sawmillUsersLoading) => [
        ...sawmillUsersLoading,
        activeSawmillUser,
      ]);

      dispatch(
        sawmillActions.removeSawmillUser(
          sawmill.id,
          activeSawmillUser.email,
          (error) => {
            error && dispatch(notificationActions.enqueueErrorResponse(error));
            setSawmillUsersLoading((sawmillUsersLoading) =>
              sawmillUsersLoading.filter(
                (sawmillUser) => sawmillUser !== activeSawmillUser,
              ),
            );
          },
        ),
      );
    }
  };

  const handleShowContextActions = (
    event: React.MouseEvent<HTMLButtonElement>,
    sawmillUser?: SawmillUserResponse,
  ) => {
    setActiveSawmillUser(sawmillUser);
    setAnchorEl(event.currentTarget);
  };

  const handleHideContextActions = () => {
    setAnchorEl(null);
  };

  return (
    <Card>
      <CardHeader
        title={sawmill.name}
        action={
          <IconButton
            onClick={(e) => handleShowContextActions(e)}
            color="secondary"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        }
      />
      <CardContent className={classes.content}>
        <TableContainer>
          <Table aria-label="organization table" className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>
                  {t('organization_page.users.table.user_name')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('organization_page.users.table.user_email')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('organization_page.users.table.user_phone')}
                </TableCell>
                <TableCell
                  align="right"
                  className={classes.tableHeadCell}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sawmill.users.map(
                (sawmillUser: SawmillUserResponse, index: number) => (
                  <TableRow
                    key={index}
                    hover={true}
                    className={classes.tableRow}
                  >
                    <TableCell className={classes.textBold}>
                      {sawmillUser.name}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {sawmillUser.email}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {sawmillUser.phone}
                    </TableCell>
                    <TableCell align="right">
                      <ButtonSpinner
                        isLoading={sawmillUsersLoading.includes(sawmillUser)}
                      >
                        <IconButton
                          onClick={(e) =>
                            handleShowContextActions(e, sawmillUser)
                          }
                          color="secondary"
                          className={classes.actionsButton}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </ButtonSpinner>
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
      <Menu
        keepMounted
        open={anchorEl != null}
        onClose={handleHideContextActions}
        anchorEl={anchorEl}
        classes={{
          paper: classes.menu,
        }}
      >
        {activeSawmillUser ? (
          <>
            <MenuItem onClick={handleEditSawmillUser}>
              {t('common.edit')}
            </MenuItem>
            <MenuItem onClick={handleDeleteSawmillUser}>
              {t('common.delete')}
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={handleEditSawmill}>{t('common.edit')}</MenuItem>
            <MenuItem onClick={handleDeleteSawmill}>
              {t('common.delete')}
            </MenuItem>
            <MenuItem onClick={handleAddSawmillUser}>
              {t('organization_page.users.labels.add_user')}
            </MenuItem>
          </>
        )}
      </Menu>

      <CreateEditSawmillUserDialog
        isOpen={isDialogOpen}
        handleClose={handleCloseDialog}
        sawmillId={sawmill.id}
        sawmillUser={activeSawmillUser}
      />
    </Card>
  );
};

export { SawmillCard };
